import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import CodeBlock from '../components/CodeBlock';

const cotCode = `def chain_of_thought_prompt(question):
    return f"""Question: {question}

Let's solve this step by step:
1. Identify what we know
2. Determine what we need to find
3. Apply relevant principles
4. Calculate or derive the answer
5. Verify the solution

Solution:"""`;

const CoTSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Breaking down problems into explicit reasoning steps"
      >
        Chain-of-Thought (CoT) Prompting
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Code */}
          <div>
            <CodeBlock 
              code={cotCode}
              title="cot_prompting.py"
            />
          </div>
          
          {/* Example */}
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Example: Subnet Calculation</h3>
            
            <div className="slide-card">
              <div className="mb-4">
                <span className="tech-badge">Question</span>
                <p className="text-foreground mt-2">How many usable IPs in 192.168.1.0/26?</p>
              </div>
              
              <div className="border-t border-border pt-4">
                <span className="tech-badge secondary">CoT Response</span>
                <div className="mt-3 space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    <span className="text-primary">1.</span> /26 = 26 network bits, 6 host bits (32-26=6)
                  </p>
                  <p className="text-muted-foreground">
                    <span className="text-primary">2.</span> Total addresses = 2^6 = <span className="text-foreground">64</span>
                  </p>
                  <p className="text-muted-foreground">
                    <span className="text-primary">3.</span> Usable = 64 - 2 = <span className="text-accent">62</span>
                  </p>
                  <p className="text-muted-foreground">
                    <span className="text-primary">4.</span> Network: .0, Broadcast: .63
                  </p>
                  <p className="text-muted-foreground">
                    <span className="text-primary">5.</span> Range: .1 to .62 ✓
                  </p>
                </div>
                
                <div className="mt-4 p-3 rounded-lg bg-accent/10 border border-accent/20">
                  <span className="font-heading font-semibold text-accent">Answer: 62 usable IP addresses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Benefits */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {[
            { title: 'Transparency', desc: 'Audit the reasoning process' },
            { title: 'Accuracy', desc: 'Catches errors in intermediate steps' },
            { title: 'Generalization', desc: 'Works on novel problems, not just memorized patterns' },
          ].map((benefit, index) => (
            <div key={index} className="slide-card text-center">
              <div className="font-heading font-semibold text-primary">{benefit.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{benefit.desc}</div>
            </div>
          ))}
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default CoTSlide;
